export type BrandHouseId = "cooghi" | "gb" | "babycare";

export type BrandHouse = {
  id: BrandHouseId;
  name: string;
  brand: string;
  focus: string;
  promise: string;
  warranty: string;
  reject: string;
};

export type BrandHouseMatchable = {
  title: string;
  subcategory?: string;
  tags?: string[];
};

export const brandHouses: BrandHouse[] = [
  {
    id: "cooghi",
    name: "酷骑馆",
    brand: "COOGHI",
    focus: "S3 平衡车",
    promise: "只收明确型号，结构件必须可验",
    warranty: "车架、轮组、限位 30 天",
    reject: "鱼店铺货、无实拍、轮组异常"
  },
  {
    id: "gb",
    name: "gb 馆",
    brand: "好孩子",
    focus: "轻便折叠推车",
    promise: "折叠、刹车、卡扣当场能验",
    warranty: "折叠与刹车 15 天",
    reject: "布套严重破损、刹车失灵、缺件"
  },
  {
    id: "babycare",
    name: "babycare 馆",
    brand: "babycare",
    focus: "腰凳 / 背带",
    promise: "接触面深洗，承托结构完整",
    warranty: "扣具与承托 15 天",
    reject: "扣具缺失、无洗标、承托变形"
  }
];

const MATCHERS: Array<{ id: BrandHouseId; pattern: RegExp }> = [
  { id: "cooghi", pattern: /酷骑|cooghi|s3.{0,8}平衡车|平衡车.{0,8}s3/i },
  { id: "gb", pattern: /好孩子|\bgb\b|口袋车|轻便推车|伞车|折叠推车/i },
  { id: "babycare", pattern: /babycare|腰凳|背带|抱娃/i }
];

export function matchBrandHouse(item: string | BrandHouseMatchable): BrandHouse | undefined {
  const haystack =
    typeof item === "string"
      ? item
      : [item.title, item.subcategory ?? "", ...(item.tags ?? [])].join(" ");

  const matched = MATCHERS.find((entry) => entry.pattern.test(haystack));
  return matched ? brandHouses.find((house) => house.id === matched.id) : undefined;
}

export function groupProductsByHouse<T extends BrandHouseMatchable>(products: T[]) {
  const byId: Record<BrandHouseId, T[]> = {
    cooghi: [],
    gb: [],
    babycare: []
  };
  const other: T[] = [];

  for (const product of products) {
    const house = matchBrandHouse(product);
    if (house) {
      byId[house.id].push(product);
    } else {
      other.push(product);
    }
  }

  return { byId, other };
}

export type SourcingPulse = {
  id: BrandHouseId | "other";
  name: string;
  watching: number;
  inspect: number;
  bid: number;
  total: number;
};

export type FairOffer = {
  headline: string;
  note: string;
  belowReference: boolean;
};

type SourcingAction = "watch" | "bid" | "inspect" | string;

function countActions<T extends { action?: SourcingAction }>(items: T[]): Omit<SourcingPulse, "id" | "name"> {
  return {
    watching: items.filter((item) => item.action === "watch").length,
    inspect: items.filter((item) => item.action === "inspect").length,
    bid: items.filter((item) => item.action === "bid").length,
    total: items.length
  };
}

export function buildSourcingPulse<T extends BrandHouseMatchable & { action?: SourcingAction }>(
  opportunities: T[]
): SourcingPulse[] {
  const grouped = groupProductsByHouse(opportunities);
  const pulses = brandHouses.map((house) => ({
    id: house.id,
    name: house.name,
    ...countActions(grouped.byId[house.id])
  }));
  const other = countActions(grouped.other);

  return other.total > 0 ? [...pulses, { id: "other", name: "其他", ...other }] : pulses;
}

export function describeFairOffer(product: {
  title?: string;
  subcategory?: string;
  tags?: string[];
  price: number;
  originalPrice: number;
  condition: string;
  safetyScore: number;
  warrantyDays: number;
}): FairOffer {
  const belowReference = product.originalPrice > product.price;
  const house = matchBrandHouse({
    title: product.title ?? "",
    subcategory: product.subcategory,
    tags: product.tags
  });

  if (belowReference) {
    return {
      headline: "好价",
      note: house
        ? `低于成色参考，按${house.name}质检和成色定价，不打超低价幌子`
        : "低于成色参考，按质检和成色定价，不打超低价幌子",
      belowReference
    };
  }

  return {
    headline: "质检定价",
    note: house
      ? `${house.name}按成色、安全分 ${product.safetyScore} 和 ${product.warrantyDays} 天质保定价`
      : `按成色、安全分 ${product.safetyScore} 和 ${product.warrantyDays} 天质保定价`,
    belowReference
  };
}
